// src/components/programs/ProgramForm/Step1Sections/ProductsPanel.jsx

import React, { useState, useEffect } from "react"; // 1. استبدل useMemo بـ useEffect
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Stack,
    alpha,
    useTheme,
} from "@mui/material";
import { Inventory as InventoryIcon } from "@mui/icons-material";
import useGetCategoriesForSelect from "../../../../hooks/categories/useGetCategoriesForSelect";
import useGetSuppliersForSelect from "../../../../hooks/suppliers/useGetSuppliersForSelect";
import useGetCitiesForSelect from "../../../../hooks/cities/useGetCitiesForSelect";
import ProductFilters from "./ProductsPanel/ProductFilters";
import ProductsPagination from "./ProductsPanel/ProductsPagination";
import ProductsList from "./ProductsPanel/ProductsList";
import useGetProducts from "../../../../hooks/products/useGetProducts";

const ProductsPanel = ({
    isArabic,
    activeLang,
}) => {
    const theme = useTheme();
    
    // 1. حالة الفلاتر الظاهرة (للـ UI فقط)
    const [filters, setFilters] = useState({
        search: "",
        category_id: "all",
        supplier_id: "all",
        city_id: "all",
    });

    // 2. حالة الفلاتر الفعلية (للـ API)
    const [activeFilters, setActiveFilters] = useState(filters);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 12;

    // 3. Debouncing Effect Logic ✅ (هذا هو الجزء الأهم)
    useEffect(() => {
        // ننشئ مؤقت ينتظر نصف ثانية
        const timer = setTimeout(() => {
            const params = { ...filters };

            // تطبيق شرط الحرفين
            if (params.search && params.search.trim().length < 2) {
                params.search = ""; // إلغاء البحث إذا كان أقل من 2
            }

            // تحديث الفلاتر التي ستذهب للـ API
            const apiParams = { ...params };
            if (apiParams.category_id === 'all') apiParams.category_id = '';
            if (apiParams.supplier_id === 'all') apiParams.supplier_id = '';
            if (apiParams.city_id === 'all') apiParams.city_id = '';
            
            setActiveFilters(apiParams);
        }, 500); // 500ms تأخير

        // تنظيف المؤقت إذا كتب المستخدم حرفاً جديداً قبل انتهاء الوقت
        return () => clearTimeout(timer);
    }, [filters]);

    // 4. استخدام activeFilters مع الـ Hook
    const { products, meta, isLoading, isFetching } = useGetProducts(
        currentPage,
        perPage,
        activeFilters
    );

    // 5. Filter out hotel products from the list
    const filteredProducts = React.useMemo(() => {
        const items = products?.items || [];
        const filtered = items.filter(p => {
            const typeCode = p.productType?.code || p.product_type?.code || '';
            return typeCode !== 'hotel';
        });
        return { ...products, items: filtered };
    }, [products]);

    // Get categories, suppliers and cities
    const { options: categories, isLoading: loadingCategories } = useGetCategoriesForSelect();
    const { suppliers, isLoading: loadingSuppliers } = useGetSuppliersForSelect();
    const { options: cities, isLoading: loadingCities } = useGetCitiesForSelect();

    // Transform options (يمكن استخدام useMemo هنا كالمعتاد لتحسين الأداء)
    const categoriesOptions = React.useMemo(() => {
        return categories.map(cat => ({
            id: cat.value,
            name: cat.label,
        }));
    }, [categories]);

    const suppliersOptions = React.useMemo(() => {
        return suppliers.map(sup => ({
            id: sup.value,
            name: sup.label,
        }));
    }, [suppliers]);

    const citiesOptions = React.useMemo(() => {
        return cities.map(city => ({
            id: city.value,
            name: city.label,
        }));
    }, [cities]);

    /**
     * Handle filter change
     */
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
        }));
        // ملاحظة: إعادة الصفحة للأولى ستتم، ولكن تحميل البيانات سيتأخر قليلاً بفضل الـ debounce
        if(field !== 'search') setCurrentPage(1); // يفضل إعادة الصفحة فقط عند تغيير الفلاتر
        else if (value.length >= 2 || value.length === 0) setCurrentPage(1);
    };

    /**
     * Handle page change
     */
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    return (
        <Paper
            elevation={2}
            sx={{
                height: 'calc(100vh - 100px)',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            }}
        >
            {/* ... Header Code (نفس الكود السابق) ... */}
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <InventoryIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.95rem', lineHeight: 1.2 }}>
                            {isArabic ? 'المنتجات المتاحة' : 'Available Products'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {isArabic ? 'اسحب المنتجات إلى الأيام' : 'Drag products to days'}
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {/* Filters Section */}
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <ProductFilters
                    filters={filters} // ✅ نمرر الـ filters الأصلية ليتمكن المستخدم من الكتابة بحرية
                    onFilterChange={handleFilterChange}
                    categoriesOptions={categoriesOptions}
                    suppliersOptions={suppliersOptions}
                    citiesOptions={citiesOptions}
                    loadingCategories={loadingCategories}
                    loadingSuppliers={loadingSuppliers}
                    loadingCities={loadingCities}
                    isArabic={isArabic}
                />
            </Box>

            {/* Products List Section */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: 1.5,
                    bgcolor: 'background.default',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        bgcolor: alpha(theme.palette.text.primary, 0.2),
                        borderRadius: '3px',
                        '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.3) },
                    },
                }}
            >
                {/* ملاحظة: isFetching مهم هنا لأنه يظهر اللودينج عند البحث الجديد
                    حتى لو كانت هناك بيانات قديمة موجودة
                */}
                {isLoading || (isFetching && !filteredProducts?.items?.length) ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : (
                    <Box sx={{ opacity: isFetching ? 0.5 : 1, transition: '0.2s' }}>
                        <ProductsList
                            products={filteredProducts?.items}
                            isArabic={isArabic}
                            activeLang={activeLang}
                            isFetching={isFetching}
                        />
                    </Box>
                )}
            </Box>

            {/* Pagination Section */}
            {!isLoading && filteredProducts?.items?.length > 0 && (
                <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.default', display: 'flex', justifyContent: 'center' }}>
                    <ProductsPagination
                        currentPage={currentPage}
                        totalPages={meta?.last_page || 1}
                        onPageChange={handlePageChange}
                        isArabic={isArabic}
                    />
                </Box>
            )}
        </Paper>
    );
};

export default ProductsPanel;